import { useMutation } from "@tanstack/react-query";

import useApiHelper from "../hooks/useApiHelper";
import { DefaultResponseWithData } from "../models/system";
import { IOfferUpdateQueueResult } from "../models/OfferUpdate";

interface IResetBestOfferBody {
  offerIds: string[];
  percent: number;
  useCategoryThreshold: boolean;
}

interface IUpdateTitlesBody {
  items: { offerId: string; title: string }[];
}

interface IUpdatePoliciesBody {
  offerIds: string[];
  locationKey?: string;
  paymentPolicyId?: string;
  fulfillmentPolicyId?: string;
  returnPolicyId?: string;
}

interface IUpdateDescriptionsBody {
  offerIds: string[];
}

interface IUpdateConditionDescriptionsBody {
  offerIds: string[];
}

// Queues background eBay offer updates (reset / title / policy / description). Per-row status is not fetched here —
// it rides along on the listings query (each row carries its latest status, like batchItem).
export default function useOfferUpdate() {
  const { post } = useApiHelper();

  const resetBestOffer = useMutation((body: IResetBestOfferBody) =>
    post<IResetBestOfferBody, DefaultResponseWithData<IOfferUpdateQueueResult>>(
      "/offerupdate/reset-best-offer",
      body
    )
  );

  const updateTitles = useMutation((body: IUpdateTitlesBody) =>
    post<IUpdateTitlesBody, DefaultResponseWithData<IOfferUpdateQueueResult>>(
      "/offerupdate/update-titles",
      body
    )
  );

  const updatePolicies = useMutation((body: IUpdatePoliciesBody) =>
    post<IUpdatePoliciesBody, DefaultResponseWithData<IOfferUpdateQueueResult>>(
      "/offerupdate/update-policies",
      body
    )
  );

  // Admin only on the API: re-renders each description from the current product template.
  const updateDescriptions = useMutation((body: IUpdateDescriptionsBody) =>
    post<IUpdateDescriptionsBody, DefaultResponseWithData<IOfferUpdateQueueResult>>(
      "/offerupdate/update-descriptions",
      body
    )
  );

  // Admin only on the API: fills each condition description from the customer's Condition Description
  // template, but only where it is blank, "Used -" or still holds placeholders.
  const updateConditionDescriptions = useMutation((body: IUpdateConditionDescriptionsBody) =>
    post<IUpdateConditionDescriptionsBody, DefaultResponseWithData<IOfferUpdateQueueResult>>(
      "/offerupdate/update-condition-descriptions",
      body
    )
  );

  return {
    resetBestOffer,
    updateTitles,
    updatePolicies,
    updateDescriptions,
    updateConditionDescriptions,
  };
}
